import { useEffect } from 'react';
import {
  usePlaidLink,
  type PlaidLinkOnSuccessMetadata,
  type PlaidLinkStableEvent,
  type PlaidLinkError,
  PlaidAccount,
} from 'react-plaid-link';

type PlaidErrorCode = 'TOO_MANY_VERIFICATION_ATTEMPTS';
type PlaidVerificationStatus = 'pending_manual_verification';

export interface CustomPlaidLinkError extends Omit<PlaidLinkError, 'error_code'> {
  error_code: PlaidErrorCode;
}

export interface CustomPlaidLinkOnSuccessMetadata {
  account: PlaidAccount & {
    verification_status: PlaidVerificationStatus;
  };
}

interface PlaidProps {
  token: string;
  onSuccess: (publicToken: string, metadata: CustomPlaidLinkOnSuccessMetadata) => void;
  onEvent?: (event: PlaidLinkStableEvent | string) => void;
  onExit?: (exitError: CustomPlaidLinkError | null) => void;
}

/**
 * Plaid Component
 *
 * This component provides a streamlined interface for using Plaid Link. It handles the initialization and display
 * of the Plaid Link flow, allowing users to connect their bank accounts. It is configured to automatically open
 * the Plaid modal when the component is ready.
 *
 * @param {PlaidProps} props - The props for the component.
 * @returns {JSX.Element} A React Fragment.
 */
const Plaid = ({ token, onSuccess, onExit, onEvent }: PlaidProps) => {
  const handleExit = (exitError: PlaidLinkError | null) => {
    if (onExit) {
      onExit(exitError as CustomPlaidLinkError | null);
    }

    return undefined;
  };

  const handleSuccess = (plaidPublicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
    /**
     * Extracting the first (and only) account from the array because we have the
     * "Enabled for one account" configuration enabled in the Plaid Dashboard.
     * This configuration ensures that metadata.accounts will always contain
     * exactly 1 account, making the use of accounts[0] safe.
     */
    const plaidConnectedAccount = metadata.accounts[0];
    const plaidMetadata: CustomPlaidLinkOnSuccessMetadata = {
      account: {
        ...plaidConnectedAccount,
        verification_status: plaidConnectedAccount.verification_status as PlaidVerificationStatus,
      },
    };

    onSuccess(plaidPublicToken, plaidMetadata);
  };

  const { open, ready } = usePlaidLink({
    token,
    onSuccess: handleSuccess,
    onExit: handleExit,
    onEvent,
  });

  useEffect(() => {
    if (ready) open();
  }, [open, ready]);

  return <></>;
};

export default Plaid;
